import { useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { AutoSizer, Size } from 'react-virtualized';
import { INDICATOR } from '@voladeum/client';
import { Chart as ChartJS, registerables } from 'chart.js';
import { CandleData } from 'client/voladeum/candles/types';
import { selectSignal } from 'ducks/signal/selectors';
import { useIndicatorContext } from 'pages/context/IndicatorContext';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useAppSelector } from 'store/hooks';
import { filterTimeseries, getMinMax } from 'utils/arrays';
import { getLineAxis, getLineDataset, getTimeseriesAxis } from 'utils/charts';

import 'chartjs-adapter-moment';

import cn from './IndicatorsGraph.module.scss';

ChartJS.register(...registerables);

export const ATRGraph = ({
  candles,
  visible,
}: {
  candles: CandleData[];
  visible: { start: number; end: number };
}) => {
  const atrRef = useRef<ChartJS>(null);
  const signals = useAppSelector(selectSignal);

  const { indicators: strategy_indicators } = useStrategyContext();
  const { indicators: signal_indicators } = useIndicatorContext();

  const atr = useMemo(() => {
    const combined_indicators = [...strategy_indicators, ...signal_indicators];
    return combined_indicators
      .filter((indicator) => indicator.indicator_id === INDICATOR.ATR)
      .flatMap((indicator) => getLineDataset(candles, signals, indicator));
  }, [candles, signal_indicators, signals, strategy_indicators]);

  const { min, max } = useMemo(() => {
    const values = atr.flatMap((dataset) => filterTimeseries(dataset.data, visible));
    return getMinMax(values);
  }, [atr, visible]);

  const atr_options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: getTimeseriesAxis({ min: visible.start, max: visible.end, display: false }),
      line: getLineAxis({ min, max, buffer: 0.2 }),
    },
    plugins: { legend: { display: false } },
  };

  return (
    <AutoSizer>
      {({ height, width }: Size) => (
        <div className={cn.graph} style={{ height, width }}>
          <Chart
            key={`atr-${height}-${width}`}
            ref={atrRef}
            type="line"
            data={{ labels: [], datasets: atr }}
            options={atr_options}
            width={width}
            height={height}
          />
        </div>
      )}
    </AutoSizer>
  );
};
